<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { ChatAgent, ChatPresentation, ChatSurface, ResourceLayout } from "../types/chat";
import { chatContextIdentity } from "../lib/chat-context";
import { resolveChatPresentation } from "../lib/presentation";
import ResourcePreview from "./blocks/resource-preview.vue";

/**
 * The single integration surface for chat. Drop `<ChatExperience>` into a page
 * (admin) or context layout (candidate) and it owns the whole flow:
 *
 *  - resolves the governing `agent` (`useChatAgent`) — admin always has a system
 *    agent; candidate only when an `agent` file governs the browsed context;
 *  - owns the conversation/stream (`useChat`) so the seed message typed into the
 *    floating input and every takeover message land in the *same* conversation;
 *  - toggles between the collapsed `<ChatFloatingInput>` and the full-screen
 *    `<ChatTakeover>`. **Back keeps the conversation** (no reset/abort).
 *
 * The `resource` slot is forwarded to the takeover for the pinned report /
 * assessment view (filled in B6/B7).
 */
const props = withDefaults(
  defineProps<{
    surface: ChatSurface;
    /** Active site id; sent as `x-site-id` and used to resolve the agent. */
    siteId?: string;
    /** Resource path the chat is scoped to (candidate agent gating). */
    contextPath?: string;
    /** The user the bound resource is about (report subject, e.g. from the `?user=` query). */
    subjectUserId?: string;
    /** Active report-provider block id in the bound admin report editor. */
    reportProviderId?: string;
    /** Short label for the governing resource. */
    contextLabel?: string;
    /** Whether a resource governs this chat (defaults to "is the resource slot filled?"). */
    hasResource?: boolean;
    /** Override the always-on admin system agent. */
    systemAgent?: ChatAgent;
    /** Force a resource layout, overriding the agent preference. */
    layoutOverride?: ResourceLayout;
    /**
     * How the expanded chat is presented:
     *  - `takeover` (default): full-screen modal — used on the candidate surface;
     *  - `dock`: a right-hand side panel that sits beside the live page (reflowed
     *    via `useChatDock`) so the user keeps context — used on the admin surface.
     *  - `responsive`: candidate split/auto contexts use that dock, falling back
     *    to its full-width modal at narrow widths; inline contexts use takeover.
     */
    presentation?: ChatPresentation;
    /**
     * Where the collapsed trigger button renders:
     *  - `floating` (default): fixed bottom-right corner (candidate).
     *  - `header`: teleported into `#chat-trigger-target` in the host layout (admin).
     */
    triggerPlacement?: "floating" | "header";
  }>(),
  {
    siteId: undefined,
    contextPath: undefined,
    subjectUserId: undefined,
    reportProviderId: undefined,
    contextLabel: undefined,
    hasResource: undefined,
    systemAgent: undefined,
    layoutOverride: undefined,
    presentation: "takeover",
    triggerPlacement: "floating",
  },
);

const {
  available,
  agent: resolvedAgent,
  loading: availabilityLoading,
} = useChatAvailability(
  props.surface,
  () => props.siteId,
  { contextPath: () => props.contextPath },
);
const agent = computed(
  () => resolvedAgent.value
    ?? (props.surface === "admin" ? props.systemAgent ?? null : null),
);

const emit = defineEmits<{
  (e: "agentResolved", agent: ChatAgent | null): void;
}>();

// Emit agent state whenever it changes so parent can adjust layout (e.g. padding).
watch(agent, (val) => emit("agentResolved", val), { immediate: true });

// Agent picker (admin only): lists available agents for the current context
// so the user can switch between the default admin agent and custom agents.
const {
  agents: availableAgents,
  selectedAgent,
  selectedAgentPath,
  hasChoice: hasAgentChoice,
  selectAgent,
} = useChatAgentPicker(props.surface, () => props.siteId, () => props.contextPath);

// When the user switches agents, reset the conversation so the next message
// starts a new conversation with the chosen agent.
function onAgentSelect(path: string) {
  selectAgent(path);
  reset();
}

// Whether chat can actually be started (an agent governs the context AND a
// provider credential is configured). There is no platform default, so an
// unconfigured site/agent disables the input with a clue instead of failing
// only after the user types.
// Only meaningful once an agent is present (the input is hidden otherwise).
// Defaults to disabled while availability resolves to avoid an enabled→disabled
// flicker on load.
const inputDisabled = computed(
  () => !!agent.value && (availabilityLoading.value || !available.value),
);

// Effective agent path: explicit picker selection takes priority; otherwise, if the
// resolved system agent has a file (i.e. `agents/system` exists), use that path so
// StartChat uses the system agent rather than the bare built-in default.
const effectiveAgentPath = computed(() => {
  if (selectedAgentPath.value !== null) return selectedAgentPath.value;
  return agent.value?.file ?? null;
});

// Agent-config-driven experience (candidate guided assessment). Defaults preserve today's behaviour:
// free-text composer + follow the server report redirect on completion.
const inputMode = computed(() => agent.value?.input ?? "freeText");
const completionMode = computed(() => agent.value?.completion ?? "navigate");
const showToolDetails = computed(() =>
  selectedAgentPath.value !== null
    ? selectedAgent.value?.showToolDetails === true
    : agent.value?.showToolDetails === true,
);
const resolvedPresentation = computed(() => resolveChatPresentation({
  presentation: props.presentation,
  surface: props.surface,
  layoutOverride: props.layoutOverride,
  agentLayout: agent.value?.layout,
}));
const contextIdentity = computed(
  () => chatContextIdentity(
    props.contextPath,
    props.reportProviderId,
    props.subjectUserId,
  ),
);

const { messages, isStreaming, hasPendingTool, resourceArtifact, completion, send, begin, cancel, reset, submitToolResult, cancelPendingTool, confirmTool } = useChat(
  props.surface,
  () => props.siteId,
  {
    contextPath: () => props.contextPath,
    subjectUserId: () => props.subjectUserId,
    reportProviderId: () => props.reportProviderId,
    agentPath: effectiveAgentPath,
    onComplete: (result) => {
      // Default (`navigate`): follow the report redirect the server resolved from the assessment's
      // submit block. When the agent opts into an in-chat report (`completion: "report"`), stay in the
      // takeover and let the host render its own report from `runId`/`reportFile` (no redirect).
      if (completionMode.value === "navigate" && result.navigation?.url) {
        navigateTo(result.navigation.url, { external: result.navigation.external });
      }
    },
  },
);

const expanded = ref(false);
const draft = ref("");
const buttonRef = ref<{ focus: () => void } | null>(null);

// Tracks an explicit user dismissal so an auto-open agent doesn't immediately re-open after the
// user leaves the takeover. Auto-open is a once-per-context entry, not a sticky state. Reset when
// any pinned resource identity changes so each report subject gets its own auto-open.
const userDismissed = ref(false);
watch(
  contextIdentity,
  () => {
    userDismissed.value = false;
  },
);

// Auto-open the takeover when the agent requests it (`defaultOpen`, candidate guided assessment),
// once the agent has resolved and chat is actually available. Client-only and one-shot per context
// (respects a prior user dismissal).
watch(
  [agent, available, availabilityLoading, () => expanded.value, userDismissed],
  ([resolvedAgent, isAvailable, loading, isExpanded, dismissed]) => {
    if (!import.meta.client) return;
    if (isExpanded || dismissed) return;
    if (!resolvedAgent?.defaultOpen) return;
    if (loading || !isAvailable) return;
    void openChat();
  },
  { immediate: true },
);

// Auto-start the conversation when the takeover opens for an `autoStart` agent: stream the agent's
// opening turn from the configured seed without a visible user bubble. `begin()` is idempotent (it
// no-ops once a conversation exists), so re-opening a started conversation never re-seeds it.
watch(expanded, (isExpanded) => {
  if (!isExpanded || !import.meta.client) return;
  if (agent.value?.autoStart && available.value) {
    void begin(agent.value.initialMessage ?? "Start");
  }
});

// The effective agent name for the button label.
const agentName = computed(() => agent.value?.name ?? "Assistant");
const agentIcon = computed(() => agent.value?.icon);

// Whether the trigger button should be visible at all.
const isMounted = ref(false);
onMounted(() => { isMounted.value = true; });
const showTrigger = computed(() => isMounted.value && agent.value !== null);

// In dock presentation, mirror the expanded state into the shared store so the
// host layout can reflow beside the panel. The responsive candidate mode resolves
// to a dock for split/auto contexts and a takeover for explicit inline contexts.
const { open: dockOpen, close: closeDock } = useChatDock();
watch(
  [expanded, resolvedPresentation],
  ([isExpanded, presentation]) => {
    dockOpen.value = presentation === "dock" && isExpanded;
  },
  { immediate: true },
);

watch(contextIdentity, (identity, previousIdentity) => {
  if (identity !== previousIdentity) {
    expanded.value = false;
    closeDock();
    reset();
  }
});

onBeforeUnmount(() => {
  closeDock();
});

const { t } = useI18n({ useScope: "local" });

/** Open the chat takeover (click on AI button). */
async function openChat() {
  draft.value = "";
  expanded.value = true;
  await nextTick();
}

async function back() {
  expanded.value = false;
  userDismissed.value = true;
  await nextTick();
  buttonRef.value?.focus();
}
</script>

<template>
  <!-- Collapsed trigger: AI button (teleported to header in admin, floating in candidate). -->
  <Teleport v-if="!expanded && showTrigger && triggerPlacement === 'header'" to="#chat-trigger-target">
    <ChatAiButton
      ref="buttonRef"
      :agent-name="agentName"
      :agent-icon="agentIcon"
      :disabled="inputDisabled"
      :disabled-reason="inputDisabled && !availabilityLoading ? t('notConfigured') : undefined"
      placement="header"
      @click="openChat"
    />
  </Teleport>

  <ChatAiButton
    v-if="!expanded && showTrigger && triggerPlacement === 'floating'"
    ref="buttonRef"
    :agent-name="agentName"
    :agent-icon="agentIcon"
    :disabled="inputDisabled"
    :disabled-reason="inputDisabled && !availabilityLoading ? t('notConfigured') : undefined"
    placement="floating"
    @click="openChat"
  />

  <ChatTakeover
    v-if="expanded"
    :agent="agent"
    :surface="surface"
    :presentation="resolvedPresentation"
    :messages="messages"
    :busy="isStreaming"
    :context-label="contextLabel"
    :initial-draft="draft"
    :has-resource="hasResource ?? (!!$slots.resource || !!resourceArtifact)"
    :input-blocked="hasPendingTool"
    :input-hint="t('inputHint')"
    :input-mode="inputMode"
    :completion="completion"
    :completion-mode="completionMode"
    :show-tool-details="showToolDetails"
    :layout-override="layoutOverride"
    @send="(text: string) => send(text)"
    @cancel="cancel"
    @back="back"
    @submit-tool="(payload) => submitToolResult(payload.invocationId, payload.toolName, payload.result)"
    @cancel-tool="(payload) => cancelPendingTool(payload.invocationId, payload.toolName)"
    @confirm-tool="(payload) => confirmTool(payload.invocationId, payload.toolName, payload.approve, payload.summary)"
  >
    <template v-if="$slots.resource || resourceArtifact" #resource="slotProps">
      <slot name="resource" v-bind="{ ...slotProps, artifact: resourceArtifact }">
        <ResourcePreview v-if="resourceArtifact" :artifact="resourceArtifact" :compact="slotProps.compact" />
      </slot>
    </template>
    <template v-if="$slots.report" #report="slotProps">
      <slot name="report" v-bind="slotProps" />
    </template>
  </ChatTakeover>
</template>

<i18n lang="yaml">
en:
  inputHint: Answer the prompt above to continue.
  notConfigured: Chat isn't configured yet — add a provider API key in site settings to enable it.
</i18n>
